import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import rootReducer from './rootReducer';
import {
  persistStore,
  persistReducer,
  createMigrate,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import { bulkStorage } from '../utils/storage';
import { migrations } from './migration';

// Persist full puzzle data locally so archive puzzles don't require re-fetching.
// Storage uses IndexedDB (via localforage) which can handle the ~10 MB payload.
const gameTransform = createTransform(
  // Transform on persist (write) — keep all puzzle data
  (inboundState: any) => {
    if (!inboundState?.archive) return inboundState;
    return {
      ...inboundState,
      archive: inboundState.archive.map((g: any) => ({
        id: g.id,
        idx: g.idx,
        board: g.board || [],
        words: g.words || [],
        bonusWords: g.bonusWords || [],
        definitions: g.definitions || [],
        x5: g.x5 || '',
        foundWords: g.foundWords || [],
        foundBonusWords: g.foundBonusWords || [],
        hintsLeft: g.hintsLeft ?? 3,
        letterStartTriggered: g.letterStartTriggered ?? false,
        letterTotalTriggered: g.letterTotalTriggered ?? false,
        shareUsed: g.shareUsed ?? false,
        gridSize: g.gridSize ?? (g.board?.length > 0 ? g.board.length : undefined),
        totalWords: g.totalWords ?? (g.words?.length > 0 ? g.words.length : undefined),
      })),
    };
  },
  // Transform on rehydrate (read) — pass through as-is
  (outboundState: any) => outboundState,
  { whitelist: ['game'] },
);

const persistConfig = {
  timeout: 0,
  key: 'root',
  version: 5,
  storage: bulkStorage,
  whitelist: ['options', 'game'],
  stateReconciler: autoMergeLevel2,
  migrate: createMigrate(migrations, { debug: false }),
  transforms: [gameTransform],
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
  persistConfig,
  rootReducer,
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: import.meta.env.DEV,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: import.meta.env.DEV,
});

const persistedStore = persistStore(store);

//store for provider
export { store, persistedStore };

//redux types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

//Typed hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
